import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import {
  FaMapMarkerAlt,
  FaPaperPlane,
  FaGlobeAmericas,
  FaChevronRight,
  FaCheckCircle,
  FaStar,
  FaChevronDown,
  FaTimesCircle,
  FaBus,
  FaHotel,
  FaUtensils,
  FaShieldAlt,
  FaMountain,
  FaLandmark,
} from "react-icons/fa";

import "./Hampi.css";

const Hampi = () => {
  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({
      top: ref.current.offsetTop - 100,
      behavior: "smooth",
    });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  const itineraryData = [
    {
      day: 1,
      title: "Arrival in Hospet & Sacred Core Exploration",
      points: [
        "Arrival at Hospet and transfer to hotel.",
        "Visit Virupaksha Temple.",
        "Explore Hampi Bazaar.",
        "Visit Sasivekalu Ganesha.",
        "Visit Kadalekalu Ganesha.",
        "Sunset at Hemakuta Hill.",
        "Overnight stay in Hampi / Hospet.",
      ],
      icon: <FaLandmark />,
    },

    {
      day: 2,
      title: "Vittala Temple & Riverside Monuments",
      points: [
        "Visit Vittala Temple.",
        "Explore the famous Stone Chariot.",
        "Visit King’s Balance.",
        "Optional coracle ride on Tungabhadra River.",
        "Evening free for cafés and market exploration.",
        "Overnight stay in Hampi / Hospet.",
      ],
      icon: <FaLandmark />,
    },

    {
      day: 3,
      title: "Royal Enclosure & Zenana Area",
      points: [
        "Visit Lotus Mahal.",
        "Explore Elephant Stables.",
        "Visit Hazara Rama Temple.",
        "Explore Queen’s Bath.",
        "Optional deeper exploration of Royal Enclosure.",
        "Evening at leisure.",
        "Overnight stay in Hampi / Hospet.",
      ],
      icon: <FaLandmark />,
    },

    {
      day: 4,
      title: "Sunrise at Matanga Hill & Departure",
      points: [
        "Early morning trek to Matanga Hill.",
        "Enjoy panoramic sunrise views.",
        "Optional visit to Anjanadri Hill.",
        "Return to hotel for breakfast.",
        "Departure to Hospet.",
      ],
      icon: <FaMountain />,
    },
  ];

  return (
    <div className="trip-container">

      {/* HEADER */}
      <motion.header
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="trip-header"
      >
        <div className="header-left">

          <h1 className="trip-title">
            Hampi Heritage Tour
          </h1>

          <p className="trip-duration">
            3 Nights / 4 Days
          </p>

        </div>

        <div className="header-right">

          <div className="info-row">
            <FaMapMarkerAlt className="header-icon" />

            <div className="info-text">
              <span>Destinations:</span>

              <p>
                Hospet • Hampi • Vittala Temple • Matanga Hill
              </p>
            </div>
          </div>

          <div className="info-row">
            <FaPaperPlane className="header-icon" />

            <div className="info-text">
              <span>Start & End:</span>
              <p>Hospet</p>
            </div>
          </div>

          <div className="info-row type-tag">
            <FaGlobeAmericas className="header-icon" />
            <p>Heritage • Temples • Architecture • Nature</p>
          </div>

        </div>
      </motion.header>

      {/* IMAGE GRID */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false }}
        variants={fadeInUp}
        className="image-grid-container"
      >

        <div className="grid-item large-vertical">
          <img
            src="https://images.unsplash.com/photo-1689946727963-be60e05fe278?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            alt="Hampi Temple"
          />
        </div>

        <div className="grid-item middle-box">
          <img
            src="https://plus.unsplash.com/premium_photo-1661963629241-52c812d5c7f8?q=80&w=387&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            alt="Temple Architecture"
          />
        </div>

        <div className="right-stack">

          <div className="grid-item small-h">
            <img
              src="https://images.unsplash.com/photo-1616606484004-5ef3cc46e39d?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Stone Chariot"
            />
          </div>

          <div className="grid-item small-h">
            <img
              src="https://images.unsplash.com/photo-1561981969-65ee8dfc7351?q=80&w=871&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
              alt="Hampi Landscape"
            />
          </div>

        </div>
      </motion.div>

      {/* STICKY NAV */}
      <nav className="sticky-nav">

        <button
          className="tab-link"
          onClick={() => scrollToSection(itineraryRef)}
        >
          Detailed Itinerary
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(highlightsRef)}
        >
          Highlights
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(inclusionsRef)}
        >
          Policy & Inclusions
        </button>

        <button
          className="tab-link"
          onClick={() => scrollToSection(aboutRef)}
        >
          About Trip
        </button>

      </nav>

      <div className="content-section-wrapper">

        {/* ITINERARY */}
        <section ref={itineraryRef} className="scroll-section">

          <h2 className="section-main-title">
            Tour Itinerary
          </h2>

          <div className="days-list">

            {itineraryData.map((item) => (
              <motion.div
                key={item.day}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.2 }}
                variants={fadeInUp}
                className="day-block"
              >

                <div className="day-header">

                  <span className="day-badge">
                    Day {item.day}
                  </span>

                  <h3>{item.title}</h3>

                </div>

                <div className="day-body">

                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>
                  ))}

                </div>
              </motion.div>
            ))}

          </div>
        </section>

        {/* HIGHLIGHTS */}
        <motion.section
          ref={highlightsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            Trip Highlights
          </h2>

          <div className="highlights-grid">

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Visit Virupaksha Temple</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Explore Vittala Temple & Stone Chariot</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Royal architecture at Lotus Mahal</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Sunset at Hemakuta Hill</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Sunrise from Matanga Hill</p>
            </div>

            <div className="h-item">
              <FaStar className="star-icon" />
              <p>Optional coracle ride experience</p>
            </div>

          </div>
        </motion.section>

        {/* INCLUSIONS */}
        <motion.section
          ref={inclusionsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <div className="policy-box inclusion-box">

            <h4 className="p-title green">
              <FaCheckCircle />
              What's Included
            </h4>

            <ul className="p-list">

              <li>
                <FaUtensils />
                Daily breakfast and dinner
              </li>

              <li>
                <FaHotel />
                Accommodation for 3 nights
              </li>

              <li>
                <FaBus />
                Local sightseeing and transfers
              </li>

              <li>
                <FaCheckCircle />
                Driver charges, fuel, toll taxes and parking
              </li>

              <li>
                <FaShieldAlt />
                Tour coordination and assistance
              </li>

            </ul>
          </div>

          <div className="policy-box exclusion-box">

            <h4 className="p-title red">
              <FaTimesCircle />
              What's Excluded
            </h4>

            <ul className="p-list">

              <li>
                <FaTimesCircle />
                Train or flight tickets
              </li>

              <li>
                <FaTimesCircle />
                Monument entry fees and camera charges
              </li>

              <li>
                <FaTimesCircle />
                Coracle ride charges
              </li>

              <li>
                <FaUtensils />
                Meals not specifically mentioned
              </li>

              <li>
                <FaTimesCircle />
                Personal expenses and shopping
              </li>

              <li>
                <FaShieldAlt />
                Additional expenses due to unforeseen circumstances
              </li>

            </ul>
          </div>

        </motion.section>

        {/* ABOUT */}
        <motion.section
          ref={aboutRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false }}
          variants={fadeInUp}
          className="scroll-section"
        >

          <h2 className="section-main-title">
            About This Journey
          </h2>

          <p className="about-text">
            The Hampi Heritage Tour is a thoughtfully paced journey through
            one of India’s most remarkable UNESCO World Heritage Sites.
            <br /><br />
            This experience combines sacred temples, royal enclosures,
            riverside monuments, scenic viewpoints, and the iconic
            boulder-strewn landscapes of the Vijayanagara Empire.
            Carefully planned sightseeing with adequate rest periods ensures
            a relaxed and enriching heritage experience for every traveler.
          </p>

        </motion.section>

        {/* TERMS */}
        <div className="accordion-item">

          <Link
            to="/terms-conditions"
            className="accordion-header"
            style={{
              textDecoration: "none",
              color: "inherit",
            }}
          >

            <span>Terms & Conditions</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: "rotate(-90deg)" }}
            />

          </Link>
        </div>

        {/* PRIVACY */}
        <div className="accordion-item">

          <Link
            to="/privacy-policy"
            className="accordion-header"
            style={{
              textDecoration: "none",
              color: "inherit",
            }}
          >

            <span>Privacy Policy</span>

            <FaChevronDown
              className="arrow-icon"
              style={{ transform: "rotate(-90deg)" }}
            />

          </Link>
        </div>

      </div>
    </div>
  );
};

export default Hampi;